"use strict";
import { InterfaceElementName } from "@uniswap/analytics-events";
import { AddressDisplay } from "components/AccountDetails/AddressDisplay";
import { ActionTile } from "components/AccountDrawer/ActionTile";
import IconButton, { IconWithConfirmTextButton } from "components/AccountDrawer/IconButton";
import { EmptyWallet } from "components/AccountDrawer/MiniPortfolio/EmptyWallet";
import MiniPortfolio from "components/AccountDrawer/MiniPortfolio/MiniPortfolio";
import { useAccountDrawer } from "components/AccountDrawer/MiniPortfolio/hooks";
import { ModalState, miniPortfolioModalStateAtom } from "components/AccountDrawer/constants";
import { LimitedSupportBanner } from "components/Banner/LimitedSupportBanner";
import { Power } from "components/Icons/Power";
import { Settings } from "components/Icons/Settings";
import { TopazIdStatusIcon } from "components/Identicon/TopazIdStatusIcon";
import { ReceiveCryptoModal } from "components/ReceiveCryptoModal";
import { WalletModeToggle } from "components/AccountDrawer/WalletModeToggle";
import DelegationMismatchModal from "components/delegation/DelegationMismatchModal";
import Row from "components/deprecated/Row";
import { isTopazIdConnectorId, usePrivyAuth } from "components/Web3Provider/privy/PrivyWagmiBridge";
import { useDisconnect } from "hooks/useDisconnect";
import { useModalState } from "hooks/useModalState";
import { TOPAZ_ID_SETTINGS_URL, topazIdName, topazIdProfileUrl, useTopazIdProfile } from "hooks/useTopazIdProfile";
import { useAtom } from "jotai";
import { useAccount as useAccountWagmi } from "wagmi";
import { useCallback, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useUserHasAvailableClaim, useUserUnclaimedAmount } from "state/claim/hooks";
import { CopyHelper } from "theme/components/CopyHelper";
import { ExternalLink } from "theme/components/Links";
import { Button, Flex, Text, useMedia } from "ui/src";
import { Swap } from "ui/src/components/icons/Swap";
import { ArrowDownCircleFilled } from "ui/src/components/icons/ArrowDownCircleFilled";
import { TestnetModeBanner } from "features/testnets/TestnetModeBanner";
import { useEnabledChains } from "features/chains/hooks/useEnabledChains";
import { usePortfolioBalances, usePortfolioTotalValue } from "features/dataApi/balances";
import { useENSName } from "features/ens/api";
import { setIsTestnetModeEnabled } from "features/settings/slice";
import Trace from "features/telemetry/Trace";
import { ModalName } from "features/telemetry/constants";
import { useUnitagByAddress } from "features/unitags/hooks";
import { capturePostHogEvent, clearPostHogWallet } from "posthog";
import { TestID } from "test/fixtures/testIDs";
import { shortenAddress } from "utilities/src/addresses";
import { useEvent } from "utilities/src/react/hooks";
import { isPathBlocked } from "utils/blockedPaths";
export default function AuthenticatedHeader({ account, openSettings }) {
  const { disconnect } = useDisconnect();
  const { data: ENSName } = useENSName(account);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const media = useMedia();
  const [modalState, setModalState] = useAtom(miniPortfolioModalStateAtom);
  const shouldShowBuyFiatButton = !isPathBlocked("/buy");
  const { isTestnetModeEnabled } = useEnabledChains();
  const unclaimedAmount = useUserUnclaimedAmount(account);
  const isUnclaimed = useUserHasAvailableClaim(account);
  const { toggleModal: toggleClaimModal } = useModalState(ModalName.AddressClaim);
  const accountDrawer = useAccountDrawer();
  const dispatch = useDispatch();
  const privyAuth = usePrivyAuth();
  const { connector } = useAccountWagmi();
  const isProvisioningSmartWallet = Boolean(
    privyAuth && isTopazIdConnectorId(connector?.id) && privyAuth.walletMode === "smart" && !privyAuth.smartWalletReady
  );
  const handleDisconnect = useCallback(() => {
    capturePostHogEvent("wallet_disconnected", {
      connector_id: connector?.id,
      wallet_address: account.toLowerCase()
    });
    clearPostHogWallet();
    dispatch(setIsTestnetModeEnabled(false));
    if (isTopazIdConnectorId(connector?.id) && privyAuth) {
      void privyAuth.logout();
    } else {
      disconnect();
    }
    accountDrawer.close();
  }, [account, dispatch, disconnect, accountDrawer, connector?.id, privyAuth]);
  const handleSwapCryptoClick = useCallback(() => {
    accountDrawer.close();
    navigate(`/swap`, { replace: true });
  }, [accountDrawer, navigate]);
  const openAddressQRModal = useEvent(() => setModalState(ModalState.QR_CODE));
  const openCEXTransferModal = useEvent(() => setModalState(ModalState.CEX_TRANSFER));
  const openReceiveCryptoModal = useEvent(() => setModalState(ModalState.DEFAULT));
  const { data: balancesById } = usePortfolioBalances({ address: account });
  const hasAnyTokens = balancesById && Object.keys(balancesById).length > 0;
  const { data: portfolioData } = usePortfolioTotalValue({ address: account });
  const balanceUSD = portfolioData?.balanceUSD;
  const isPortfolioZero = !isTestnetModeEnabled && balanceUSD === 0 && !hasAnyTokens;
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const shouldShowDelegationMismatch = false;
  const [displayDelegationMismatchModal, setDisplayDelegationMismatchModal] = useState(false);
  const { unitag } = useUnitagByAddress(account);
  const { data: topazProfile } = useTopazIdProfile(account);
  const topazName = topazIdName(topazProfile);
  const showAddress = topazName || ENSName || unitag?.username;
  const amount = unclaimedAmount?.toFixed(0, { groupSeparator: "," });
  return <Flex overflow="hidden">
      <TestnetModeBanner />
      <Flex flex={1} px="$padding16" py={"$spacing20"}>
        <Flex row justifyContent="space-between" alignItems="flex-start" mb="$spacing8">
          <Flex row gap="$spacing8" alignItems="center">
            {
    /* <IconButton
      hideHorizontal={false}
      data-testid="close-account-drawer"
      onClick={accountDrawer.close}
      Icon={ArrowRight}
    /> */
  }
            <Flex row gap={"$spacing8"} alignItems="center">
              <TopazIdStatusIcon account={account} size={media.lg ? 32 : 40} />
              <Text
    variant={media.lg ? "body2" : "body1"}
    fontWeight={"$medium"}
    whiteSpace="nowrap"
    overflow="hidden"
    textOverflow="ellipsis"
    maxWidth={media.lg ? 100 : 150}
  >
                {shortenAddress(account)}
              </Text>
            </Flex>
          </Flex>
          <Flex row gap="$spacing8">
            <IconButton
    hideHorizontal={showDisconnectConfirm}
    data-testid="wallet-settings"
    onClick={openSettings}
    Icon={Settings}
  />
            <Trace logPress element={InterfaceElementName.DISCONNECT_WALLET_BUTTON}>
              <IconWithConfirmTextButton
    data-testid="wallet-disconnect"
    onConfirm={handleDisconnect}
    onShowConfirm={setShowDisconnectConfirm}
    Icon={Power}
    text={t("common.button.disconnect")}
    dismissOnHoverOut
  />
            </Trace>
          </Flex>
        </Flex>
        {isProvisioningSmartWallet ? <Flex gap="$spacing8" mt="$spacing16" alignItems="center">
            <Text variant="subheading2" color="$neutral1">
              Finalizing your smart wallet…
            </Text>
            <Text variant="body3" color="$neutral2" textAlign="center">
              Your Topaz ID smart wallet is being set up. Your deposit address and balances appear here in a moment.
            </Text>
            <WalletModeToggle />
          </Flex> : <>
            <Flex gap="$spacing4">
              <Text variant="subheading1" color="$neutral1">
                <AddressDisplay enableCopyAddress={!showAddress} address={account} />
              </Text>
          {showAddress && <CopyHelper iconSize={14} iconPosition="right" toCopy={account}>
              <Text variant="body3" color="neutral3">
                {shortenAddress(account)}
              </Text>
            </CopyHelper>}
          {
    /* Topaz ID management only makes sense for a Topaz ID session — a natively-connected EOA
       has no Topaz ID, so don't invite them to "set one up" for an address that can't have one. */
  }
          {isTopazIdConnectorId(connector?.id) && <Flex row gap="$spacing12" mt="$spacing4">
              <ExternalLink
    href={topazProfile?.found ? topazIdProfileUrl(account) : TOPAZ_ID_SETTINGS_URL}
    style={{ textDecoration: "none" }}
  >
                <Text variant="body4" color="$accent1">
                  {topazProfile?.found ? "View Topaz ID profile \u2197" : "Set up your Topaz ID \u2197"}
                </Text>
              </ExternalLink>
              {topazProfile?.found && <ExternalLink href={TOPAZ_ID_SETTINGS_URL} style={{ textDecoration: "none" }}>
                  <Text variant="body4" color="$neutral2">
                    Edit ↗
                  </Text>
                </ExternalLink>}
            </Flex>}
          <WalletModeToggle />
        </Flex>
        <Flex flex={1} mt="$spacing16">
          {shouldShowDelegationMismatch && <LimitedSupportBanner onPress={() => setDisplayDelegationMismatchModal(true)} />}
          {isPortfolioZero ? <EmptyWallet
    handleSwapCryptoClick={handleSwapCryptoClick}
    handleReceiveCryptoClick={openAddressQRModal}
    handleCEXTransferClick={openCEXTransferModal}
  /> : <>
              <Row gap="8px">
                {shouldShowBuyFiatButton && <ActionTile
    dataTestId={TestID.WalletBuyCrypto}
    Icon={<Swap size={24} color="$neutral2Temp" />}
    name={"Swap"}
    onClick={handleSwapCryptoClick}
    errorMessage={t("common.restricted.region")}
    errorTooltip={t("moonpay.restricted.region")}
  />}
                <ActionTile
    dataTestId={TestID.WalletReceiveCrypto}
    Icon={<ArrowDownCircleFilled size={24} color="$neutral2Temp" />}
    name={t("common.receive")}
    onClick={openReceiveCryptoModal}
  />
              </Row>
              <MiniPortfolio account={account} />
            </>}
          {isUnclaimed && <Button
    my="$spacing8"
    fill={false}
    onPress={toggleClaimModal}
    style={{ background: "linear-gradient(to right, #9139b0 0%, #4261d6 100%)" }}
  >
              <Trans i18nKey="account.authHeader.claimReward" values={{ amount }} />
            </Button>}
            </Flex>
          </>}
      </Flex>
      {modalState !== void 0 && <ReceiveCryptoModal />}
      {displayDelegationMismatchModal && <DelegationMismatchModal onClose={() => setDisplayDelegationMismatchModal(false)} />}
    </Flex>;
}
